import React, { FC } from 'react';
import { DomotzNetworkInterface } from '../../../interfaces/NetworkDevices';

const PORTS_PER_ROW = 12;

interface SimpleInterfaceStatus {
  id: number;
  interfaceName: string;
  operationalStatus: string;
  portId: number;
  fullLabel: string;
}

interface props {
  data: DomotzNetworkInterface[];
}

function chunkArray<T>(array: T[], size: number): T[][] {
  const chunkedArr: T[][] = [];
  let index = 0;
  while (index < array.length) {
    chunkedArr.push(array.slice(index, index + size));
    index += size;
  }
  return chunkedArr;
}

function extractInterfaceInfo(item: DomotzNetworkInterface): {
  interfaceName: string;
  portId: number;
} {
  const fullInterfaceName = item.label.split(' - ')[0];
  const pathParts = item.path.split('/');

  const tableIndex = pathParts.findIndex(
    (part) => part === 'network_interfaces_table'
  );

  let portId = 0;
  if (tableIndex !== -1 && tableIndex + 1 < pathParts.length) {
    portId = parseInt(pathParts[tableIndex + 1], 10);
    if (isNaN(portId)) {
      portId = 0;
    }
  }

  return {
    interfaceName: fullInterfaceName,
    portId: portId,
  };
}

function processInterfaceStatuses(
  data: DomotzNetworkInterface[]
): SimpleInterfaceStatus[] {
  // 1. Filter out "notPresent" values
  const filteredData = data.filter((item) => item.value !== 'notPresent');

  // 2. Transform the data
  let transformedData: SimpleInterfaceStatus[] = filteredData
    .map((item) => {
      const { interfaceName, portId } = extractInterfaceInfo(item);
      return {
        id: item.id,
        interfaceName: interfaceName,
        operationalStatus: item.value || 'unknown',
        portId: portId,
        fullLabel: item.label,
      };
    })
    .filter((item) => item.portId > 0);

  // 3. Filter out interfaces where interfaceName contains 'oob' (case-insensitive)
  transformedData = transformedData.filter(
    (item) => !item.interfaceName.toLowerCase().includes('oob')
  );

  // 4. Sort by portId numerically
  transformedData.sort((a, b) => a.portId - b.portId);

  return transformedData;
}

const SmallPortDisplay: FC<{ portData: SimpleInterfaceStatus }> = ({
  portData,
}) => {
  const { interfaceName, operationalStatus, portId } = portData;

  let statusClasses = '';
  if (operationalStatus === 'up') {
    statusClasses =
      'bg-green-200 border-green-400 hover:bg-green-300 text-green-700';
  } else {
    statusClasses =
      'bg-yellow-100 border-yellow-300 hover:bg-yellow-200 text-yellow-700';
  }

  return (
    <div
      className={`w-10 h-10 flex items-center justify-center text-sm font-bold rounded-sm border-b-2 transition duration-150 ${statusClasses}`}
      title={`${interfaceName} (ID: ${portId} | Status: ${operationalStatus})`}
    >
      {portId}
    </div>
  );
};

const NetworkDeviceInterfaces: FC<props> = ({ data }) => {
  const processedData = processInterfaceStatuses(data);
  const portRows = chunkArray(processedData, PORTS_PER_ROW);

  return (
    <div>
      <div className="border border-gray-200 p-2 dark:border-gray-700 rounded-md">
        {portRows.map((row, index) => (
          <div
            key={index}
            className={`flex flex-row justify-center gap-1 mb-1 last:mb-0 ${portRows.length > 1 ? 'p-1  rounded-sm' : ''}`}
          >
            {row.map((portData) => (
              <SmallPortDisplay key={portData.id} portData={portData} />
            ))}
          </div>
        ))}
      </div>

      {processedData.length === 0 && (
        <p className="text-gray-500 italic mt-4">
          No active network interfaces found (or all are "notPresent" or "oob").
        </p>
      )}
    </div>
  );
};

export default NetworkDeviceInterfaces;
